import React from "react";
import { Box, Button, ButtonProps, IconButton, IconButtonProps } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";

type AdminActionButtonProps = Omit<ButtonProps, "disabled"> & {
  loading?: boolean;
  disabled?: boolean;
};

/** Primary admin actions: thin linear bar at the bottom instead of a spinner. */
const AdminActionButton = ({
  loading = false,
  disabled = false,
  children,
  sx,
  fullWidth,
  ...rest
}: AdminActionButtonProps) => (
  <Box
    sx={{
      position: "relative",
      ...(fullWidth
        ? { display: "block", width: "100%" }
        : { display: "inline-flex", verticalAlign: "middle" }),
    }}
  >
    <Button
      {...rest}
      fullWidth={fullWidth}
      disabled={loading || disabled}
      sx={{ position: "relative", ...sx }}
    >
      {children}
    </Button>
    {loading && (
      <LinearProgress
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 3,
          borderRadius: "0 0 4px 4px",
          pointerEvents: "none",
        }}
      />
    )}
  </Box>
);

export default AdminActionButton;

type AdminIconButtonProps = Omit<IconButtonProps, "disabled"> & {
  loading?: boolean;
  disabled?: boolean;
};

/** Icon-only actions with the same bottom progress bar. */
export const AdminIconButton = ({
  loading = false,
  disabled = false,
  children,
  sx,
  ...rest
}: AdminIconButtonProps) => (
  <Box sx={{ position: "relative", display: "inline-flex", verticalAlign: "middle" }}>
    <IconButton
      {...rest}
      disabled={loading || disabled}
      sx={{ position: "relative", borderRadius: 1, ...sx }}
    >
      {children}
    </IconButton>
    {loading && (
      <LinearProgress
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 2,
          borderRadius: "0 0 4px 4px",
          pointerEvents: "none",
        }}
      />
    )}
  </Box>
);
